import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AutoRedemptionSettingFacade } from '@features/auto-redemption-settings/facades/auto-redemption-setting/auto-redemption-setting.facade';
import {
  AutoRedemptionSettingTypes,
  CreateAutoRedemptionSettingSuccess,
} from '@features/auto-redemption-settings/store/actions/auto-redemption-settings.actions';
import { PointsAccountFacade } from '@features/points-account/facades/points-account/points-account.facade';
import {
  LoadPointsAccountsSuccess,
  NoopPointsAccountSelectionEnded,
  PointsAccountActionTypes,
} from '@features/points-account/store/actions/points-account.actions';
import { UserPreferenceFacade } from '@features/user-preference/facades/user-preference/user-preference.facade';
import { UnleashService } from '@karelics/angular-unleash-proxy-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TypedAction } from '@ngrx/store/src/models';
import { UserCustomizationsFacade } from '@rd/core/facades/user-customizations-facade/user-customizations.facade';
import { DateService } from '@rd/core/services/date/date.service';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import {
  AutoRedemptionSetting,
  HttpError,
  PointsAccount,
  UserCustomizationsModel,
  UserPreference,
} from '@rd/types';
import dayjs from 'dayjs';
import {
  combineLatest,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { ANZVelocityAutoRedemptionSetupPromptComponent } from '../components/velocity-auto-redemption-setup-prompt/velocity-auto-redemption-setup-prompt.component';

@Injectable()
export class AnzVelocityAutoRedemptionEnrollmentEffects {
  constructor(
    private actions$: Actions,
    private userPreferenceFacade: UserPreferenceFacade,
    private userCustomizationsFacade: UserCustomizationsFacade,
    private pointsAccountFacade: PointsAccountFacade,
    private autoRedemptionSettingFacade: AutoRedemptionSettingFacade,
    private dialogService: DialogService,
    private dateService: DateService,
    private router: Router,
    private unleashService: UnleashService,
  ) {}

  openVelocityAutoRedemptionPopUp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          PointsAccountActionTypes.LOAD_POINTS_ACCOUNTS_SUCCESS,
          NoopPointsAccountSelectionEnded,
        ),
        // TODO: remove this Feature flag check once fully rolled out on ANZ
        filter(() =>
          this.unleashService.isEnabled('rd_anz_velocity_quasi_direct_earn'),
        ),
        // NOTE: there is a race condition where autoRedemptionSetting or user customizations is not loaded yet, so we need to wait until it is loaded
        switchMap((action) =>
          combineLatest([
            this.userCustomizationsFacade.userCustomizationsLoading$,
            this.autoRedemptionSettingFacade.getAutoRedemptionSettingsLoading$,
          ]).pipe(
            filter(
              ([userCustomizationsLoading, autoRedemptionSettingsLoading]) =>
                !userCustomizationsLoading && !autoRedemptionSettingsLoading,
            ),
            take(1),
            map(() => action),
          ),
        ),
        withLatestFrom(
          combineLatest([
            this.userPreferenceFacade.userPreferences$,
            this.pointsAccountFacade.pointsAccounts$,
            this.userCustomizationsFacade.userCustomizations$,
            this.autoRedemptionSettingFacade.autoRedemptionSetting$,
            this.autoRedemptionSettingFacade.getAutoRedemptionSettingsError$,
            this.userCustomizationsFacade.userCustomizationError$,
          ]),
        ),
        tap(
          ([
            _action,
            [
              userPreference,
              pointsAccounts,
              userCustomizations,
              autoRedemptionSetting,
              autoRedemptionSettingsError,
              userCustomizationError,
            ],
          ]: [
            (
              | LoadPointsAccountsSuccess
              | TypedAction<'[PointsAccounts] No-op: points account selection ended '>
            ),
            [
              UserPreference,
              PointsAccount[],
              UserCustomizationsModel,
              AutoRedemptionSetting | null,
              HttpError | null,
              HttpError | null,
            ],
          ]) => {
            const { pointsAccountId } = userPreference;

            if (
              !pointsAccountId ||
              this.router.url.startsWith('/select-card') ||
              !this.isTheFirstPointsAccount(pointsAccountId, pointsAccounts) ||
              !!autoRedemptionSettingsError ||
              !!userCustomizationError
            ) {
              return;
            }

            const { velocityQuasiDirectEarn } = userCustomizations;
            const {
              velocityQuasiDirectEarnSetupOnce,
              velocityQuasiDirectEarnPromptDismissed,
              velocityQuasiDirectEarnPromptShownAt,
            } = velocityQuasiDirectEarn || {};

            const hasSetupVelocityAutoRedemption =
              autoRedemptionSetting?.productSku === 'VAVELOCITYFREQUENTFLYER';

            if (
              hasSetupVelocityAutoRedemption &&
              autoRedemptionSetting?.pointsAccountId === pointsAccountId &&
              !velocityQuasiDirectEarnSetupOnce?.includes(pointsAccountId)
            ) {
              this.userCustomizationsFacade.putUserCustomizations({
                ...userCustomizations,
                velocityQuasiDirectEarn: {
                  ...userCustomizations.velocityQuasiDirectEarn,
                  velocityQuasiDirectEarnSetupOnce:
                    velocityQuasiDirectEarnSetupOnce
                      ? [...velocityQuasiDirectEarnSetupOnce, pointsAccountId]
                      : [pointsAccountId],
                },
              });

              return;
            }

            const hasVelocityOnboardingSource = pointsAccounts
              .find((pointsAccount) => pointsAccount.id === pointsAccountId)
              ?.products.some(
                (product) =>
                  product.customFields?.onboardingSource === 'velocity',
              );

            if (
              hasVelocityOnboardingSource &&
              (!velocityQuasiDirectEarn ||
                (!velocityQuasiDirectEarnSetupOnce?.includes(pointsAccountId) &&
                  !velocityQuasiDirectEarnPromptDismissed &&
                  (!velocityQuasiDirectEarnPromptShownAt ||
                    !this.dateService.isSameDate(
                      this.dateService.toUTCDateWithOffset(
                        this.dateService.dayjs().toDate(),
                      ),
                      this.dateService.toUTCDateWithOffset(
                        this.dateService
                          .dayjs(velocityQuasiDirectEarnPromptShownAt)
                          .toDate(),
                      ),
                    ))))
            ) {
              this.dialogService.open(
                ANZVelocityAutoRedemptionSetupPromptComponent,
                {
                  id: 'velocity-auto-redemption-setup-prompt',
                  screenMode: 'center-ml',
                  keepModalOnOverlayClick: true,
                  data: { userCustomizations, pointsAccountId },
                },
              );
            }
          },
        ),
      ),
    {
      dispatch: false,
    },
  );

  updateVelocityQuasiDirectEarnSetupOnce$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AutoRedemptionSettingTypes.CREATE_AUTO_REDEMPTION_SETTING_SUCCESS,
        ),
        withLatestFrom(this.userCustomizationsFacade.userCustomizations$),
        map(
          ([action, userCustomizations]: [
            CreateAutoRedemptionSettingSuccess,
            UserCustomizationsModel,
          ]) => {
            if (action.payload.productSku !== 'VAVELOCITYFREQUENTFLYER') {
              return;
            }

            const velocityQuasiDirectEarnSetupOnce =
              userCustomizations.velocityQuasiDirectEarn
                ?.velocityQuasiDirectEarnSetupOnce;
            const newVelocityQuasiDirectEarnSetupOnce =
              velocityQuasiDirectEarnSetupOnce
                ? [
                    ...velocityQuasiDirectEarnSetupOnce,
                    action.payload.pointsAccountId,
                  ]
                : [action.payload.pointsAccountId];

            return this.userCustomizationsFacade.putUserCustomizations({
              ...userCustomizations,
              velocityQuasiDirectEarn: {
                ...userCustomizations.velocityQuasiDirectEarn,
                velocityQuasiDirectEarnSetupOnce:
                  newVelocityQuasiDirectEarnSetupOnce,
              },
            });
          },
        ),
      ),
    {
      dispatch: false,
    },
  );

  private isTheFirstPointsAccount(
    currentPointsAccountId: string,
    pointsAccounts: PointsAccount[],
  ) {
    const currentPointsAccount = pointsAccounts.find(
      (pointsAccount) => pointsAccount.id === currentPointsAccountId,
    );

    return pointsAccounts.every((pointsAccount) => {
      if (pointsAccount.id === currentPointsAccountId) {
        return true;
      }

      const currentProductStartTime =
        currentPointsAccount?.products[0]?.startTime;
      const pointsAccountStartTime = pointsAccount.products[0]?.startTime;

      if (!currentProductStartTime || !pointsAccountStartTime) {
        return false;
      }

      return dayjs(currentProductStartTime).isBefore(
        dayjs(pointsAccountStartTime),
      );
    });
  }
}
